import { useEffect } from "react"
import { useQuery } from "@tanstack/react-query"
import { graphql } from "@/graphql"
import { useGraphQLClient, useSession } from "@/lib/blueprint-auth/client"
import { useMeterPowerAnalysisWizard } from "../wizard"

const ViewerQuery = graphql(`
    query Viewer {
        viewer {
            email
            givenName
            familyName
            accounts {
                ... on AccountType {
                    number
                }
            }
        }
    }
`)

type QueryParams = string | null

export function useInitMeterPowerAnalysis(
    affiliateCode: QueryParams,
    rewardCode: QueryParams,
    meterPointId: QueryParams,
    productCode: QueryParams
) {
    const { formValues, updateFormValues } = useMeterPowerAnalysisWizard()
    const graphQLClient = useGraphQLClient()
    const { data: session } = useSession()

    const { data: user } = useQuery({
        queryKey: ["useInitMeterPowerAnalysis"],
        queryFn: () => graphQLClient.request(ViewerQuery),
        select: (data) => {
            return data.viewer
        },
        enabled: !!session?.isAuthenticated && session.authMethod !== "scoped",
    })
    const currentAccount = user?.accounts?.[0] ?? null

    const isFromDashboard = !!meterPointId && !!productCode

    // Update formValues to apply affiliateCode from queryParams
    useEffect(() => {
        if (affiliateCode) {
            updateFormValues({ affiliateCode })
        }
    }, [affiliateCode, updateFormValues])

    // Update formValues to apply rewardCode from queryParams
    useEffect(() => {
        if (rewardCode) {
            updateFormValues({ rewardCode })
        }
    }, [rewardCode, updateFormValues])

    // Update formValues to apply meterPointId and productCode from queryParams
    useEffect(() => {
        if (
            meterPointId &&
            productCode &&
            (formValues.meterPointId !== meterPointId || formValues.productCode !== productCode)
        ) {
            updateFormValues({
                meterPointId,
                productCode,
            })
        }
    }, [
        meterPointId,
        productCode,
        formValues.meterPointId,
        formValues.productCode,
        updateFormValues,
    ])

    // Update formValues to apply user details if user comes from dashboard
    useEffect(() => {
        if (session?.isAuthenticated && isFromDashboard) {
            const email = user?.email ?? null
            const firstName = user?.givenName ?? null
            const lastName = user?.familyName ?? null
            const accountNumber = currentAccount?.number ?? null

            // Check if all required values are available to prevent stucking in the form
            if (email && firstName && lastName && accountNumber) {
                updateFormValues({
                    isFromDashboard,
                    user: { firstName, lastName, email, accountNumber },
                })
            }
        }
    }, [isFromDashboard, session?.isAuthenticated, updateFormValues, user, currentAccount])
}
